import {
  type BufferGeometry,
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  type Material,
  Matrix4,
  MeshStandardMaterial,
} from 'three';
import type { TileVisibility } from '@/game/sim';
import config from '../config';
import { axialToWorld } from '../coords';
import type { HexTileMap, HexTileMapKey, HexTileType } from '../model/types';
import { bevelledGeometry, flatGeometry } from './tileGeometry';
import { exploredColor, TILE_COLORS, unexploredMaterial } from './tiles/sharedMaterials';

// White, so each instance's colour (its type, dimmed out of sight) shows as is.
const landMaterial = new MeshStandardMaterial({ color: 'white' });
const waterMaterial = new MeshStandardMaterial({ color: 'white' });

const tileColors = Object.fromEntries(
  Object.entries(TILE_COLORS).map(([type, color]) => [
    type,
    { visible: new Color(color), explored: exploredColor(color) },
  ])
) as Record<HexTileType, { visible: Color; explored: Color }>;

const noRaycast = () => {};
const scratch = new Matrix4();

/**
 * One InstancedMesh holding a subset of the board's tiles, packed into the
 * first `count` slots: a tile joins at the end and leaves by moving the last
 * tile into its slot, so only the slots touched are rewritten and uploaded.
 */
class TileBatch {
  readonly mesh: InstancedMesh;
  /** Slot → tile index. */
  private readonly tileAt: Int32Array;
  /** Tile index → slot, or -1 when the tile isn't in this batch. */
  private readonly slotOf: Int32Array;
  private dirtyMin = Number.POSITIVE_INFINITY;
  private dirtyMax = -1;

  constructor(
    geometry: BufferGeometry,
    material: Material,
    capacity: number,
    tileCount: number,
    colored: boolean
  ) {
    this.mesh = new InstancedMesh(geometry, material, Math.max(1, capacity));
    this.mesh.count = 0;
    // The board fills the view; its bounds would change with every add.
    this.mesh.frustumCulled = false;
    // Picking goes through the ground plane (TilePicker), never the tiles.
    this.mesh.raycast = noRaycast;
    if (colored) {
      // Made up front so the material compiles with instance colours.
      this.mesh.instanceColor = new InstancedBufferAttribute(
        new Float32Array(Math.max(1, capacity) * 3),
        3
      );
    }
    this.tileAt = new Int32Array(Math.max(1, capacity));
    this.slotOf = new Int32Array(tileCount).fill(-1);
  }

  has(tile: number): boolean {
    return this.slotOf[tile] !== -1;
  }

  add(tile: number, x: number, z: number, color?: Color): void {
    const slot = this.mesh.count++;
    this.tileAt[slot] = tile;
    this.slotOf[tile] = slot;
    this.mesh.setMatrixAt(slot, scratch.makeTranslation(x, 0, z));
    if (color) this.mesh.setColorAt(slot, color);
    this.touch(slot);
  }

  setColor(tile: number, color: Color): void {
    const slot = this.slotOf[tile];
    this.mesh.setColorAt(slot, color);
    this.touch(slot);
  }

  remove(tile: number): void {
    const slot = this.slotOf[tile];
    const last = --this.mesh.count;
    this.slotOf[tile] = -1;
    if (slot === last) return;
    const moved = this.tileAt[last];
    this.tileAt[slot] = moved;
    this.slotOf[moved] = slot;
    this.mesh.getMatrixAt(last, scratch);
    this.mesh.setMatrixAt(slot, scratch);
    const colors = this.mesh.instanceColor;
    if (colors) colors.array.copyWithin(slot * 3, last * 3, last * 3 + 3);
    this.touch(slot);
  }

  /** Uploads only the slots written since the last flush. */
  flush(): void {
    if (this.dirtyMax < 0) return;
    const start = this.dirtyMin;
    const count = this.dirtyMax - start + 1;
    this.mesh.instanceMatrix.addUpdateRange(start * 16, count * 16);
    this.mesh.instanceMatrix.needsUpdate = true;
    const colors = this.mesh.instanceColor;
    if (colors) {
      colors.addUpdateRange(start * 3, count * 3);
      colors.needsUpdate = true;
    }
    this.dirtyMin = Number.POSITIVE_INFINITY;
    this.dirtyMax = -1;
  }

  private touch(slot: number): void {
    if (slot < this.dirtyMin) this.dirtyMin = slot;
    if (slot > this.dirtyMax) this.dirtyMax = slot;
  }
}

const UNSET = -1;
const VISIBILITY_CODE: Record<TileVisibility, number> = { unexplored: 0, explored: 1, visible: 2 };

/**
 * The board's tiles as three InstancedMeshes: bevelled land and flat water
 * (both lit, coloured per instance by type and dimmed out of sight), and the
 * unlit flat fog over every unexplored tile. Each tile is in exactly one.
 */
interface TileBoard {
  land: InstancedMesh;
  water: InstancedMesh;
  fog: InstancedMesh;
  /** Moves/recolours the tiles whose visibility changed; returns how many did. */
  update(visibility: (key: HexTileMapKey) => TileVisibility): number;
  dispose(): void;
}

function createTileBoard(tiles: HexTileMap): TileBoard {
  const entries = Array.from(tiles.entries());
  const count = entries.length;
  const keys = entries.map(([key]) => key);
  const types = entries.map(([, tile]) => tile.type);
  const xs = new Float32Array(count);
  const zs = new Float32Array(count);
  let waterCount = 0;
  entries.forEach(([, tile], i) => {
    const world = axialToWorld(tile.coordinates, config.hexMap.tile.radius);
    xs[i] = world.x;
    zs[i] = world.z;
    if (tile.type === 'water') waterCount++;
  });
  const land = new TileBatch(bevelledGeometry, landMaterial, count - waterCount, count, true);
  const water = new TileBatch(flatGeometry, waterMaterial, waterCount, count, true);
  const fog = new TileBatch(flatGeometry, unexploredMaterial, count, count, false);
  const state = new Int8Array(count).fill(UNSET);

  function update(visibility: (key: HexTileMapKey) => TileVisibility): number {
    let changed = 0;
    for (let i = 0; i < count; i++) {
      const next = VISIBILITY_CODE[visibility(keys[i])];
      const prev = state[i];
      if (next === prev) continue;
      state[i] = next;
      changed++;
      const batch = types[i] === 'water' ? water : land;
      if (next === 0) {
        if (batch.has(i)) batch.remove(i);
        fog.add(i, xs[i], zs[i]);
        continue;
      }
      const colors = tileColors[types[i]];
      const color = next === 2 ? colors.visible : colors.explored;
      if (fog.has(i)) fog.remove(i);
      if (batch.has(i)) batch.setColor(i, color);
      else batch.add(i, xs[i], zs[i], color);
    }
    land.flush();
    water.flush();
    fog.flush();
    return changed;
  }

  return {
    land: land.mesh,
    water: water.mesh,
    fog: fog.mesh,
    update,
    dispose() {
      land.mesh.dispose();
      water.mesh.dispose();
      fog.mesh.dispose();
    },
  };
}

export { createTileBoard, type TileBoard };
